<template>
  <b-container
    :class="{ 'revatech-header-shell--dropdown-open': dropdownOpen }"
    class="revatech-header-shell"
    fluid
    style="padding: 0">
    <b-navbar
      :class="[
        'revatech-navbar',
        getHeaderClass(),
        { 'revatech-navbar--dropdown-open': dropdownOpen }
      ]">
      <b-container fluid>
        <b-row v-if="selectedProcess === 'ENTRIES_PROCESS'">
          <HeaderEntries
            :has-contacts-module-access="hasContactsModuleAccess"
            :active-tab="activeTab"
            :navigate-to-contacts-module="navigateToContactsModule"
            :has-offers-module-access="hasOffersModuleAccess"
            :has-transports-module-planning-access="hasTransportsModulePlanningAccess"
            :has-treatments-module-analyses-access="hasTreatmentsModuleAnalysesAccess"
            :navigate-to-treatments-module="navigateToTreatmentsModule"
            :has-pre-invoices-module-access="hasPreInvoicesModuleAccess"
            :navigate-to-pre-invoices-module="navigateToPreInvoicesModule"
            :has-invoices-module-access="hasInvoicesModuleAccess"
            :navigate-to-invoices-module="navigateToInvoicesModule" />
        </b-row>
        <b-row v-else-if="selectedProcess === 'EXITS_PROCESS'">
          <HeaderExits
            :has-contacts-module-access="hasContactsModuleAccess"
            :active-tab="activeTab"
            :navigate-to-contacts-module="navigateToContactsModule"
            :has-exit-process-module-access="hasExitProcessModuleAccess"
            :navigate-to-exits-module="navigateToExitsModule"
            :navigate-to-flows-module="navigateToFlowsModule" />
        </b-row>
        <b-row
          align-h="end"
          no-gutters>
          <b-col md="auto">
            <b-navbar-nav class="ml-auto">
              <b-nav-item-dropdown
                id="processSelector"
                right
                @show="openDropdown"
                @hidden="closeDropdown">
                <template slot="button-content">
                  <span>
                    {{ $t(selectedProcess) }}
                  </span>
                </template>
                <b-dropdown-item
                  v-for="process in processus"
                  :key="process"
                  @click="changeProcess(process)">
                  {{ $t(process) }}
                </b-dropdown-item>
              </b-nav-item-dropdown>
            </b-navbar-nav>
          </b-col>
          <b-col md="auto">
            <b-navbar-nav class="ml-auto">
              <b-nav-item-dropdown
                id="siteSelector"
                right
                @show="openDropdown"
                @hidden="closeDropdown">
                <template slot="button-content">
                  <span>
                    {{ customLabel(selectedSite) }}
                  </span>
                </template>
                <b-dropdown-item
                  v-for="site in sites"
                  :key="site"
                  @click="changeSite(site,true)">
                  {{ customLabel(site) }}
                </b-dropdown-item>
              </b-nav-item-dropdown>
            </b-navbar-nav>
          </b-col>
          <b-col md="auto">
            <b-navbar-nav class="ml-auto">
              <b-nav-item-dropdown
                id="langSelector"
                right
                @show="openDropdown"
                @hidden="closeDropdown">
                <template slot="button-content">
                  <span>
                    <flag :iso="selectedLanguage.flag" /></span>
                </template>
                <b-dropdown-item
                  v-for="language in languages"
                  :key="language.value"
                  @click="changeLanguage(language)">
                  <flag :iso="language.flag" /> {{ language.title }}
                </b-dropdown-item>
              </b-nav-item-dropdown>
            </b-navbar-nav>
          </b-col>
          <b-col md="auto">
            <b-navbar-nav class="ml-auto">
              <b-nav-item-dropdown
                id="user"
                right
                @show="openDropdown"
                @hidden="closeDropdown">
                <template slot="button-content">
                  <span class="user-menu-trigger">
                    <v-icon name="user-circle" />
                    <em class="user-menu-name">{{ userDisplayName }}</em>
                  </span>
                </template>
                <b-dropdown-item-button @click="openProfileModal">
                  {{ $t('navBarProfile') }}
                </b-dropdown-item-button>
                <b-dropdown-item
                  v-if="isAnnouncementAdmin"
                  to="/administration/announcements">
                  {{ $t('announcements.administration') }}
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="hasSettingsAccess"
                  to="/administration/settings">
                  {{ $t('settings.menu') }}
                </b-dropdown-item>
                <b-dropdown-item
                  href="#"
                  @click="redirectToKeycloak()">
                  {{ $t('changePassword') }}
                </b-dropdown-item>
                <b-dropdown-item
                  href="#"
                  @click="signOut()">
                  {{ $t('navBarSignOut') }}
                </b-dropdown-item>
              </b-nav-item-dropdown>
            </b-navbar-nav>
          </b-col>
        </b-row>
      </b-container>
    </b-navbar>

    <b-modal
      id="user-profile-modal"
      ref="userProfileModal"
      :title="$t('profileTitle')"
      hide-footer
      centered>
      <dl class="profile-details">
        <div class="profile-details__row">
          <dt>{{ $t('profileFirstName') }}</dt>
          <dd>{{ profileFirstName }}</dd>
        </div>
        <div class="profile-details__row">
          <dt>{{ $t('profileLastName') }}</dt>
          <dd>{{ profileLastName }}</dd>
        </div>
        <div class="profile-details__row">
          <dt>{{ $t('profileEmail') }}</dt>
          <dd>{{ profileEmail }}</dd>
        </div>
        <div class="profile-details__row profile-details__row--roles">
          <dt>{{ $t('profileRoles') }}</dt>
          <dd>
            <template v-if="profileRoles.length">
              <b-badge
                v-for="role in profileRoles"
                :key="role"
                variant="secondary"
                class="profile-role-badge">
                {{ role }}
              </b-badge>
            </template>
            <span v-else>{{ $t('profileNoRoles') }}</span>
          </dd>
        </div>
      </dl>
      <div class="profile-modal-actions">
        <b-button
          variant="primary"
          @click="$refs.userProfileModal.hide()">
          {{ $t('close') }}
        </b-button>
      </div>
    </b-modal>
  </b-container>
</template>

<script>

import { updatePreference } from '../../services/userService';
import { findAllSites } from '../../services/offerService';
import Configuration from '../../model/Configuration';
import HeaderEntries from '@/components/shared/HeaderEntries.vue';
import HeaderExits from '@/components/shared/HeaderExits.vue';
import settingsSections from '../settings/sections';
const config = new Configuration();

export default {
  components: { HeaderExits, HeaderEntries },
  data() {
    return {
      languages: [ {
        flag: 'gb',
        value: 'en-GB',
        title: 'EN'
      },
      {
        flag: 'fr',
        value: 'fr-FR',
        title: 'FR'
      }
      ],
      selectedLanguage: {
        flag: 'gb',
        value: 'en-GB',
        title: 'EN'
      },
      sites: [],
      selectedSite: undefined,
      dropdownOpen: false,
      processus: [ 'ENTRIES_PROCESS', 'EXITS_PROCESS' ],
      // Persisté côté client pour garder le header cohérent au refresh.
      // Quand l'URL indique clairement un "exit" (flows/exits), on écrasera cette valeur.
      selectedProcess: (typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('processus'))
        ? window.localStorage.getItem('processus')
        : 'ENTRIES_PROCESS'
    };
  },
  computed: {
    userPreference() {
      return this.$store.state.userPreference.preferences;
    },
    userDisplayName() {
      const firstName = this.userPreference && this.userPreference.firstName ? this.userPreference.firstName : '';
      const lastName = this.userPreference && this.userPreference.lastName ? this.userPreference.lastName : '';
      const fullName = `${firstName} ${lastName}`.trim();

      if (fullName) {
        return fullName;
      }

      if (this.userPreference && this.userPreference.username) {
        return this.userPreference.username;
      }

      return 'Utilisateur inconnu';
    },
    profileFirstName() {
      return this.userPreference && this.userPreference.firstName ? this.userPreference.firstName : '-';
    },
    profileLastName() {
      return this.userPreference && this.userPreference.lastName ? this.userPreference.lastName : '-';
    },
    profileEmail() {
      return this.userPreference && this.userPreference.email ? this.userPreference.email : '-';
    },
    profileRoles() {
      return this.$store.getters['security/userRoles'];
    },
    isAnnouncementAdmin() {
      return this.$store.getters['security/isAnnouncementAdmin'];
    },
    hasSettingsAccess() {
      return settingsSections.some(section => !section.isAllowed || section.isAllowed(this.$store));
    },
    hasContactsModuleAccess() {
      return this.$store.getters['security/hasContactsRole']('read');
    },
    hasOffersModuleAccess() {
      return this.$store.getters['security/hasOffersRole']('read');
    },
    hasTransportsModulePlanningAccess() {
      return this.$store.getters['security/hasTransportsRole']('read');
    },
    hasTreatmentsModuleAnalysesAccess() {
      return this.$store.getters['security/hasTreatmentsRole']('read');
    },
    hasInvoicesModuleAccess() {
      return this.$store.getters['security/hasInvoicesRole']('read');
    },
    hasPreInvoicesModuleAccess() {
      return this.$store.getters['security/hasPreInvoicesRole']('read');
    },
    hasExitProcessModuleAccess() {
      return this.$store.getters['security/hasExitProcessRole']('read');
    },
    activeTab() {
      return this.$route.name;
    }
  },
  watch: {
    userPreference(value) {
      if (!value) return;

      const language = this.languages.filter(lg => lg.title === value.language);
      if (language[0]) {
        this.changeLanguage(language[0]);
      }

      if (value.site) {
        this.changeSite(value.site, false);
      }
    },
    '$route.name'(routeName) {
      // En cas de refresh ou deep-link, on force le header "exit" uniquement
      // quand la route l'indique clairement.
      this.applyProcessOverrideFromRoute(routeName);
    }
  },
  beforeMount() {
    if (this.$store.state.userPreference.preferences.language) {
      const language = this.languages.filter(lg => lg.title === this.$store.state.userPreference.preferences.language);
      if (language) {
        this.changeLanguage(language[0]);
      } else {
        this.changeLanguage(this.languages[0]);
      }
    } else {
      this.changeLanguage(this.languages[0]);
    }

    // Synchronise le header avec l'URL (ex: /flows*, /exits*)
    this.applyProcessOverrideFromRoute(this.$route && this.$route.name);

    findAllSites().then(sites => {
      this.sites = sites;
      const site = this.$store.state.userPreference.preferences['site'];
      if (site) {
        this.selectedSite = site;
      } else {
        this.selectedSite = 'ENGIS_DIVISION';
        this.changeSite(this.selectedSite, false);
      }
    });
  },

  methods: {
    openDropdown() {
      this.dropdownOpen = true;
    },
    closeDropdown() {
      this.dropdownOpen = false;
    },
    applyProcessOverrideFromRoute(routeName) {
      if (!routeName) return;

      // Routes "exit" = flows/exits : on doit afficher le header des sorties.
      if (routeName === 'flows' || routeName === 'exits') {
        this.selectedProcess = 'EXITS_PROCESS';
        return;
      }

      // Routes "entry" (tout sauf contacts) : on doit afficher le header des entrées.
      // NB: "contacts" est commun aux deux modes, donc on conserve le choix utilisateur.
      if (routeName !== 'contacts') {
        this.selectedProcess = 'ENTRIES_PROCESS';
      }
    },
    changeLanguage(language) {
      this.selectedLanguage = language;
      this.$i18n.locale = language.value;
      this.contactsVueInstance.$i18n.locale = language.value;
      this.offersVueInstance.$i18n.locale = language.value;
      this.deliveriesVueInstance.$i18n.locale = language.value;
      this.treatmentsVueInstance.$i18n.locale = language.value;
      this.preInvoicesVueInstance.$i18n.locale = language.value;
      this.invoicesVueInstance.$i18n.locale = language.value;
      if (this.exitProcessVueInstance && this.exitProcessVueInstance.$i18n) {
        this.exitProcessVueInstance.$i18n.locale = language.value;
      }
      this.$store.dispatch('userPreference/savePreference', { key: 'language', value: language.value });
      this.$store.dispatch('security/loadUserInfo').then(userInfo =>
        updatePreference({ id: userInfo.sub, lg: language.title }));
    },
    changeSite(site, isChangeSite) {
      this.selectedSite = site;

      this.deliveriesVueInstance.$store.dispatch('deliveryList/setSitesDelivery', { sites: [ site ], isChangeSite });
      this.deliveriesVueInstance.$store.dispatch('deliveryEditor/setSelectedSite', [ site ]);
      this.offersVueInstance.$store.dispatch('offerList/setSitesOffers', { sites: [ site ], isChangeSite });
      this.treatmentsVueInstance.$store.dispatch('treatmentList/setSitesTreatments', { sites: [ site ], isChangeSite });
      this.treatmentsVueInstance.$store.dispatch('analysisList/setSitesAnalysis', { sites:  [ site ], isChangeSite });
      this.treatmentsVueInstance.$store.dispatch('analysisEditor/setSite', site);
      this.treatmentsVueInstance.$store.dispatch('certificateList/setSitesCertificates', { sites:  [ site ], isChangeSite });
      this.exitProcessVueInstance.$store.dispatch('exitList/setSitesExits',{ sites:  [ site ], isChangeSite });
      this.exitProcessVueInstance.$store.dispatch('flowList/setSitesFlows',{ sites:  [ site ], isChangeSite });

      this.updateInvoiceSite('all',false, isChangeSite);

      this.$store.dispatch('userPreference/savePreference', { key: 'site', value: site });

      this.$store.dispatch('security/loadUserInfo').then(userInfo =>
        updatePreference({ id: userInfo.sub, lg: this.selectedLanguage.title, site }));
    },
    changeProcess(process) {
      this.selectedProcess = process;

      this.$store.dispatch('userPreference/savePreference', { key: 'processus', value: process });
      window.localStorage.setItem('processus', process);

      window.location.href = '#/contacts';
    },
    openProfileModal() {
      this.$refs.userProfileModal && this.$refs.userProfileModal.show();
    },
    redirectToKeycloak() {
      let realm = process.env.VUE_APP_KEYCLOAK_REALM;
      if (!realm) {
        if (process.env.NODE_ENV === 'production') {
          realm = 'revatech';
        } else {
          realm = 'development';
        }
      }
      window.open(`${process.env.VUE_APP_KEYCLOAK_URL}/realms/${realm}/account/password`,'_blank');
    },
    signOut() {
      this.$store.dispatch('security/logout');
    },
    navigateToContactsModule() {
      if (this.contactsVueInstance.$route.path !== '/contacts') {
        window.location.href = '#/contacts';
      }
    },
    navigateToTreatmentsModule() {
      if (this.treatmentsVueInstance.$route.path !== '/treatments') {
        window.location.href = '#/treatments';
      }
    },
    navigateToPreInvoicesModule() {
      if (this.preInvoicesVueInstance.$route.path !== '/pre-invoices') {
        this.updateInvoiceSite('pre-invoices', true);
        window.location.href = '#/pre-invoices';
      }
    },
    navigateToInvoicesModule() {
      if (this.invoicesVueInstance.$route.path !== '/invoices') {
        this.updateInvoiceSite('invoices');
        window.location.href = '#/invoices';
      }
    },
    navigateToExitsModule() {
      if (this.exitProcessVueInstance && this.exitProcessVueInstance.$route && this.exitProcessVueInstance.$route.path !== '/exits') {
        window.location.href = '#/exits';
      } else if (!this.exitProcessVueInstance) {
        window.location.href = '#/exits';
      }
    },
    navigateToFlowsModule() {
      if (this.exitProcessVueInstance && this.exitProcessVueInstance.$route && this.exitProcessVueInstance.$route.path !== '/flows') {
        window.location.href = '#/flows';
      } else if (!this.exitProcessVueInstance) {
        window.location.href = '#/flows';
      }
    },
    customLabel(key) {
      return this.$t(`SITE_${key}`);
    },
    getHeaderClass() {
      if (this.selectedSite === 'ENGIS_DIVISION') {
        return 'header-engis';
      } else if (this.selectedSite === 'MONSIN_DIVISION') {
        return 'header-monsin';
      }
      return '';
    },
    updateInvoiceSite(module = 'all', defaultStatus = false, isChangeSite) {
      if (module === 'pre-invoices') {
        const characteristics = {
          preInvoiceSites: { sites: [ this.selectedSite ], isChangeSite }
        };
        this.preInvoicesVueInstance.$store.dispatch('invoiceList/setSitesPreInvoice', { 'searchCriteria': { characteristics }, defaultStatus, module });
      }
      else if (module === 'invoices') {
        const characteristics = {
          invoiceSites: { sites: [ this.selectedSite ], isChangeSite }
        };
        this.invoicesVueInstance.$store.dispatch('invoiceList/setSitesInvoice', { 'searchCriteria': { characteristics }, defaultStatus, module });
        this.invoicesVueInstance.$store.dispatch('userPreference/savePreference', { key: 'site', value: this.selectedSite });
      }
      else {
        const characteristics = {
          invoiceSites: { sites: [ this.selectedSite ], isChangeSite },
          preInvoiceSites: { sites: [ this.selectedSite ], isChangeSite }
        };
        this.preInvoicesVueInstance.$store.dispatch('invoiceList/setSitesPreInvoice', { 'searchCriteria': { characteristics }, defaultStatus, module });
        this.invoicesVueInstance.$store.dispatch('invoiceList/setSitesInvoice', { 'searchCriteria': { characteristics }, defaultStatus, module });
        this.invoicesVueInstance.$store.dispatch('userPreference/savePreference', { key: 'site', value: this.selectedSite });
      }
    }
  }
};
</script>

<style>
.revatech-header-shell {
  position: relative;
  overflow: visible;
}
.revatech-header-shell.revatech-header-shell--dropdown-open {
  z-index: 1100 !important;
}
.revatech-navbar {
  position: relative;
  z-index: 1020;
  margin: 0.75rem 15px;
  padding: 0.45rem 0.65rem;
  overflow: visible;
  color: #243b36;
  border: 1px solid var(--revatech-border);
  border-radius: 1rem;
  background-color: #f3fbff;
  box-shadow: var(--revatech-shadow-soft);
  -webkit-backdrop-filter: blur(14px);
  backdrop-filter: blur(14px);
  transition: background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
}
.revatech-navbar--dropdown-open {
  z-index: 1045 !important;
}
.revatech-navbar > .container-fluid {
  gap: 0.35rem 1rem;
  padding-right: 0;
  padding-left: 0;
}
.revatech-navbar > .container-fluid > .row {
  align-items: center;
  gap: 0.1rem;
}
.revatech-navbar .navbar-brand {
  display: inline-flex;
  align-items: center;
  min-width: auto;
  margin-right: 0.55rem;
  padding: 0.2rem 0.35rem;
}
.revatech-navbar .nav-link {
  margin: 0 0.08rem;
  padding: 0.52rem 0.68rem !important;
  color: #294e45 !important;
  border-radius: 0.65rem;
  font-weight: 600;
  white-space: nowrap;
  transition: color 150ms ease, background-color 150ms ease, box-shadow 150ms ease, transform 150ms ease;
}
.revatech-navbar .nav-link:hover,
.revatech-navbar .nav-link:focus,
.revatech-navbar .dropdown.show > .nav-link {
  color: #173e35 !important;
  background-color: rgba(255, 255, 255, 0.5);
  box-shadow: 0 3px 10px rgba(31, 70, 61, 0.08);
  transform: translateY(-1px);
}
.revatech-navbar .nav-item .nav-link.active {
  color: #173e35 !important;
  text-decoration: none;
  background-color: rgba(255, 255, 255, 0.72);
  box-shadow: inset 0 0 0 1px rgba(47, 125, 107, 0.12), 0 4px 12px rgba(31, 70, 61, 0.09);
}
.revatech-navbar .dropdown-menu {
  z-index: 1090 !important;
  margin-top: 0.4rem;
  padding: 0.4rem;
  overflow: hidden;
  border: 1px solid var(--revatech-border);
  border-radius: 0.8rem;
  background: var(--revatech-surface-strong);
  box-shadow: 0 18px 42px rgba(21, 53, 46, 0.18);
  -webkit-backdrop-filter: blur(16px);
  backdrop-filter: blur(16px);
}
.revatech-navbar .dropdown-item {
  padding: 0.5rem 0.7rem;
  color: #294e45;
  border-radius: 0.5rem;
  font-weight: 550;
}
.revatech-navbar .dropdown-item:hover,
.revatech-navbar .dropdown-item:focus,
.revatech-navbar .dropdown-item.active,
.revatech-navbar .dropdown-item:active {
  color: #173e35;
  background-color: rgba(132, 196, 175, 0.2);
}
.user-menu-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}
.user-menu-name {
  font-style: normal;
  white-space: nowrap;
}
.profile-details {
  margin-bottom: 0;
}
.profile-details__row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}
.profile-details__row dt {
  font-weight: 600;
  margin-bottom: 0;
}
.profile-details__row dd {
  margin-bottom: 0;
  word-break: break-word;
}
.profile-role-badge {
  margin: 0 0.35rem 0.35rem 0;
}
.profile-modal-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}
.header-engis {
  border-color: rgba(47, 125, 107, 0.2);
  background-color: #D0F7EA !important;
  background-image: linear-gradient(135deg, rgba(255, 255, 255, 0.34), rgba(132, 196, 175, 0.08)) !important;
}
.header-monsin {
  border-color: rgba(174, 111, 24, 0.24);
  background-color: #FFC067 !important;
  background-image: linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(196, 126, 28, 0.07)) !important;
}
hr {
  border-top: 1px solid gray;
  margin-top: 0;
}
@media (max-width: 991.98px) {
  .revatech-navbar > .container-fluid {
    align-items: flex-start;
    flex-direction: column;
  }
  .revatech-navbar > .container-fluid > .row {
    width: 100%;
    flex-wrap: wrap;
  }
}
</style>
